import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCheck, X, Check } from "lucide-react";
import api from "@/lib/api";

// Small bell/pill widget shown in the sidebar footer. Polls incoming
// transfer requests (where current user is the current owner) every
// 60s and lets them approve/reject inline. Deliberately compact — the
// full history lives on each individual lead.
export default function TransferRequestsWidget({ collapsed = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/transfer-requests?scope=incoming&status=pending");
      setItems(data?.items || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const it = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(it); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const decide = async (req, decision) => {
    setBusyId(req.id);
    try {
      await api.post(`/transfer-requests/${req.id}/decide`, { decision });
      toast.success(decision === "approve" ? `Transferência aprovada — ${req.requester_name}` : "Pedido rejeitado");
      setItems(prev => prev.filter(x => x.id !== req.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao decidir pedido");
    } finally {
      setBusyId(null);
    }
  };

  const count = items.length;

  if (count === 0 && !open) {
    // Nothing pending — keep the DOM minimal but expose the container
    // for e2e stability.
    return <div data-testid="transfer-requests-empty" className="hidden" />;
  }

  return (
    <div className="mb-2" data-testid="transfer-requests-widget">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={collapsed ? `${count} pedido(s) de transferência` : undefined}
        data-testid="transfer-requests-toggle"
        className={`w-full inline-flex items-center ${collapsed ? "justify-center" : "gap-2 justify-between"} text-[12px] px-2 py-1.5 rounded-md transition-colors ${count > 0 ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/25 hover:bg-[hsl(var(--warning-bg))]" : "text-muted-foreground hover:bg-card"}`}
      >
        <span className="inline-flex items-center gap-2">
          <UserCheck className="w-3.5 h-3.5" />
          {!collapsed && <span>Transferências</span>}
        </span>
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-600 text-primary-foreground text-[10px] font-semibold" data-testid="transfer-requests-badge">
            {count}
          </span>
        )}
      </button>

      {open && !collapsed && (
        <div className="mt-2 rounded-md border border-border bg-card shadow-sm p-2 space-y-2 max-h-72 overflow-y-auto" data-testid="transfer-requests-list">
          {count === 0 && (
            <div className="text-[11px] text-muted-foreground py-2 px-1">Sem pedidos pendentes.</div>
          )}
          {items.map(r => (
            <div key={r.id} className="rounded-md border border-border/60 bg-muted/40 p-2" data-testid={`transfer-request-${r.id}`}>
              <div className="text-[11px] text-muted-foreground mb-1">
                <span className="font-semibold text-foreground">{r.requester_name}</span> pede{" "}
                <Link to={`/leads/${r.lead_id}`} className="underline text-primary hover:text-[#1E40AF]" data-testid={`transfer-request-lead-${r.id}`}>
                  {r.lead_name || "lead"}
                </Link>
              </div>
              {r.reason && (
                <div className="text-[11px] text-muted-foreground italic bg-card rounded px-2 py-1 border border-border/60 mb-2 line-clamp-3">
                  "{r.reason}"
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => decide(r, "approve")}
                  disabled={busyId === r.id}
                  data-testid={`transfer-request-approve-${r.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-primary-foreground disabled:opacity-50"
                >
                  <Check className="w-3 h-3" /> Aprovar
                </button>
                <button
                  type="button"
                  onClick={() => decide(r, "reject")}
                  disabled={busyId === r.id}
                  data-testid={`transfer-request-reject-${r.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1 bg-card border border-border text-foreground hover:bg-muted disabled:opacity-50"
                >
                  <X className="w-3 h-3" /> Rejeitar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
